import { ReactElement, useCallback, useState } from 'react';
import { Card, CardContent, CardActions, Grid, Alert } from '@mui/material';
import { useAwsEffect, useAwsGetter, useRoutes } from '@localstack/services';
import { LambdaInvokeCommandInput, LambdaInvokeCommandOutput } from '@localstack/types';
import { ProgressButton, LambdaFunctionInvokeForm, LambdaInvocationDetails, PageTitle } from '@localstack/ui';
import { useParams } from 'react-router-dom';

import { LambdaProps } from './types';
import { FunctionTabs } from './components/FunctionTabs';

export const LambdaFunctionInvoke = ({ Layout, clientOverrides, routes }: LambdaProps): ReactElement => {
  const { goto } = useRoutes();
  const { name: functionNameWithVersion } = useParams<'name'>();
  const [invocationDetails, setInvocationDetails] = useState<LambdaInvokeCommandOutput | undefined>(undefined);
  const { data: lambdaFunction } = useAwsGetter('Lambda', 'getFunction', [{ FunctionName: functionNameWithVersion }], {
    clientOverrides,
  });

  const { invoke, isLoading } = useAwsEffect('Lambda', ['invoke'], { clientOverrides });

  const handleCreate = useCallback(async (data: LambdaInvokeCommandInput) => {
    const response = await invoke(data);
    try {
      const logs = atob(response.LogResult || '');
      setInvocationDetails({ ...response, LogResult: logs });
    } catch (_error) {
      setInvocationDetails(response);
    }
  }, []);

  const hasCapacityProvider =
    !!lambdaFunction?.Configuration?.CapacityProviderConfig?.LambdaManagedInstancesCapacityProviderConfig
      ?.CapacityProviderArn;

  const isNonInvocable = hasCapacityProvider && lambdaFunction?.Configuration?.State === 'ActiveNonInvocable';
  // Functions with capacity providers set must only be invoked for a published version

  return (
    <Layout
      documentTitle="Lambda: Invoke Event Function"
      tabs={
        <FunctionTabs
          routes={routes}
          functionName={functionNameWithVersion}
          showScalingConfigTab={hasCapacityProvider}
        />
      }
      title={
        <PageTitle
          title={`Invoke Event Function: ${functionNameWithVersion}`}
          breadcrumbs={[
            ['Lambda', () => goto(routes.RESOURCES_LAMBDA)],
            [functionNameWithVersion, () => goto(routes.RESOURCES_LAMBDA_FUNCTION, { name: functionNameWithVersion })],
            ['Invoke ', null],
          ]}
        />
      }
      actions={
        <ProgressButton
          disabled={isNonInvocable}
          type="submit"
          form="InvokeLambdaFunction"
          variant="outlined"
          color="primary"
          loading={isLoading}
        >
          Submit
        </ProgressButton>
      }
    >
      {isNonInvocable && (
        <Alert severity="warning">
          This function cannot be invoked because it has not been published or published version is not selected.
        </Alert>
      )}
      <Grid container spacing={1}>
        <Grid item>
          {invocationDetails && (
            <Card>
              <CardContent>
                <LambdaInvocationDetails details={invocationDetails} />
              </CardContent>
            </Card>
          )}
        </Grid>
        <Grid item>
          <Card>
            <CardContent>
              <LambdaFunctionInvokeForm
                functionName={functionNameWithVersion}
                onCreate={handleCreate}
                formId="InvokeLambdaFunction"
              />
            </CardContent>
            <CardActions>
              <ProgressButton
                disabled={isNonInvocable}
                type="submit"
                form="InvokeLambdaFunction"
                variant="contained"
                color="primary"
                loading={isLoading}
              >
                Submit
              </ProgressButton>
            </CardActions>
          </Card>
        </Grid>
      </Grid>
    </Layout>
  );
};
